import useNotificationsStore from '@/store/notifications';
import { NotificationsTypes } from '@/types';
import GlobalPreloaderHelper from '@/helpers/global/globalPreloader.helper';

// URLs whose "auth required" / "not found" responses are normal flow (anonymous probes
// for current user / company). Suppress the user-facing error notification for these.
const SILENT_NOTIFICATION_URLS: ReadonlyArray<{ url: string; detail: string }> = [
  { url: '/user-profile/me/', detail: 'Authentication credentials were not provided.' },
  { url: '/customer-company/me/', detail: 'Company not found for this user.' },
];

class BaseAPIHelper {
  apiBaseUrl: string = '';

  get requestOptions(): any {
    return {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRFToken': CSRF_TOKEN
      },
      mode: 'same-origin'
    };
  }

  constructor(apiBaseUrl: string) {
    this.apiBaseUrl = apiBaseUrl;
  }

  errorsHandler(errorData: any) {
    if (errorData && Object.keys(errorData).length > 0) {
      const notificationsStore = useNotificationsStore();

      if (errorData.error_message) {
        notificationsStore.add({
          message: errorData.error_message,
          type: NotificationsTypes.error,
        });
      } else if (errorData.detail) {
        notificationsStore.add({
          message: errorData.detail,
          type: NotificationsTypes.error,
        });
      }
    }
  }

  async getBlob(url: string): Promise<Blob> {
    const options = {
      ...this.requestOptions,
      headers: new Headers({
        'X-CSRFToken': CSRF_TOKEN
      }),
      method: 'GET',
    };

    const res = await fetch(`${this.apiBaseUrl}${url}`, options);
    if (!res.ok) {
      throw new Error(`Request failed with status ${res.status}`);
    }
    return res.blob();
  }

  async get(url: string): Promise<any> {
    let response = {};

    const options = {
      ...this.requestOptions,
     headers: new Headers({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRFToken': CSRF_TOKEN
      }),
      method: 'GET',
    };

    try {
      const res = await fetch(`${this.apiBaseUrl}${url}`, options);
      response = await res.json();
    } catch (error: any) {
      response = { error: error?.message || 'Network error' };
    }

    const isSilent = SILENT_NOTIFICATION_URLS.some(
      (entry) => entry.url === url && (response as any)?.detail === entry.detail,
    );
    if (!isSilent) {
      this.errorsHandler(response);
    }

    return response;
  }

  async post(url: string, params: any): Promise<any> {
    let response: any = {};

    // Create a completely new options object instead of spreading requestOptions
    const options = {
      method: 'POST',
      ...this.requestOptions,
      headers: new Headers({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRFToken': CSRF_TOKEN
      }),
      body: params ? JSON.stringify(params) : null
    };

    try {
      const res = await fetch(`${this.apiBaseUrl}${url}`, options);
      const data = await res.json();

      response = {
        status: res.status,
        data: data, 
      };
    } catch (error: any) {
      response = {
        status: error?.status || 500,
        data: error?.data || error?.message || 'Network error',
      };
    }

    this.errorsHandler(response.data);
    return response;
  }

  async postFormData(url: string, params: any): Promise<any> {
    let response = {};

    const data = new FormData();

    Object.keys(params).forEach((key) => {
      data.append(key, params[key]);
    });

    const headersObg = { ...this.requestOptions };
    delete headersObg.headers['Content-Type'];

    const options = {
      ...headersObg,
      method: 'POST',
      body: data,
    };

    await fetch(`${this.apiBaseUrl}${url}`, options)
      .then((res) => res.json())
      .then((obj) => {
        response = obj;
      })
      .catch(() => {
        response = {};
      });

    this.errorsHandler(response);
    return Promise.resolve(response);
  }

  async postFormDataXMLHttpRequest(url: string, params: any, onUploadProgress?: (event: ProgressEvent) => void): Promise<any> {
    return new Promise((resolve, reject) => {
      const data = new FormData();
      Object.keys(params).forEach((key) => {
        data.append(key, params[key]);
      });

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.apiBaseUrl}${url}`, true);

      // Copy headers except for Content-Type
      if (this.requestOptions?.headers) {
        Object.entries(this.requestOptions.headers).forEach(([key, value]) => {
          if (key.toLowerCase() !== 'content-type') {
            xhr.setRequestHeader(key, String(value));
          }
        });
      }

      if (onUploadProgress) {
        xhr.upload.onprogress = onUploadProgress;
      }

      xhr.onload = () => {
        let response;
        try {
          response = JSON.parse(xhr.responseText);
        } catch (e) {
          response = { error: 'Invalid JSON response' };
        }
        this.errorsHandler(response);
        resolve(response);
      };

      xhr.onerror = () => {
        reject({ error: 'Upload failed' });
      };

      xhr.send(data);
    });
  }

  async postUrlEncoded(url: string, params: any): Promise<any> {
    let response = {};

    const data = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      data.append(key, params[key]);
    });

    const options = {
      ...this.requestOptions,
      method: 'POST',
      headers: {
        ...this.requestOptions.headers,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: data.toString(),
    };

    await fetch(`${this.apiBaseUrl}${url}`, options)
      .then((res) => res.json())
      .then((obj) => {
        response = obj;
      })
      .catch(() => {
        response = {};
      });

    this.errorsHandler(response);
    return Promise.resolve(response);
  }

  async put(url: string, params: any): Promise<any> {
    let response = {};

    const options = {
      ...this.requestOptions,
      method: 'PUT',
      body: JSON.stringify(params),
    };

    try {
      const res = await fetch(`${this.apiBaseUrl}${url}`, options);
      response = await res.json();
    } catch (error: any) {
      response = { error: error?.message || 'Network error' };
    }

    this.errorsHandler(response);
    return response;
  }

  async patch(url: string, params: any): Promise<any> {
    let response = {};

    const options = {
      ...this.requestOptions,
      method: 'PATCH',
      body: JSON.stringify(params),
    };

    try {
      const res = await fetch(`${this.apiBaseUrl}${url}`, options);
      response = await res.json();
    } catch (error: any) {
      response = { error: error?.message || 'Network error' };
    }

    this.errorsHandler(response);
    return response;
  }

  async delete(url: string): Promise<any> {
    let response = {};

    const options = {
      ...this.requestOptions,
      method: 'DELETE',
    };

    try {
      const res = await fetch(`${this.apiBaseUrl}${url}`, options);
      response = await res.json();
    } catch (error: any) {
      response = { error: error?.message || 'Network error' };
    }

    this.errorsHandler(response);
    return response;
  }
}

export default new BaseAPIHelper('');

let admin_api_url = '';
let website_api_url = '';

if (typeof ADMIN_API_URL !== 'undefined') {
  admin_api_url = ADMIN_API_URL;
}

if (typeof WEBSITE_API_URL !== 'undefined') {
  website_api_url = WEBSITE_API_URL;
}

class AdminBaseAPIHelper extends BaseAPIHelper {
  private async withPreloader<T>(fn: () => Promise<T>): Promise<T> {
    GlobalPreloaderHelper.show();
    try {
      return await fn();
    } finally {
      GlobalPreloaderHelper.hide();
    }
  }

  async post(url: string, params: any): Promise<any> {
    return this.withPreloader(() => super.post(url, params));
  }

  async postFormData(url: string, params: any): Promise<any> {
    return this.withPreloader(() => super.postFormData(url, params));
  }

  async put(url: string, params: any): Promise<any> {
    return this.withPreloader(() => super.put(url, params));
  }

  async patch(url: string, params: any): Promise<any> {
    return this.withPreloader(() => super.patch(url, params));
  }

  async delete(url: string): Promise<any> {
    return this.withPreloader(() => super.delete(url));
  }
}

export const AdminApiHelper = new AdminBaseAPIHelper(admin_api_url);
export const WebsiteApiHelper = new BaseAPIHelper(website_api_url);
